import React, { useEffect, useState } from 'react';
import api from '../../config/api';
import { Grid } from '@mui/material';
import {
  StyledVote,
  StyledVoteDetail,
  StyledVoteDetailParty,
} from './Detail.style';
import {
  StyledPageContainer,
  StyledPageContent,
  StyledPageWrapper,
} from '../../components/styles';
import { VoteResult } from '../../config/variables';
import { useParams } from 'react-router-dom';
import { DetailHeader } from './DetailHeader/DetailHeader';
import Visualization, { correctName } from './Visualization/Visualization';

interface HomeProps {}

export enum Party {
  ODS = 'ODS',
  KSCM = 'KSČM',
  SPD = 'SPD',
  PIR = 'Piráti',
  KDU = 'KDU-ČSL',
  CSSD = 'ČSSD',
  ANO = 'ANO 2011',
  NPB = 'Nezávislí pro Brno',
  NEZ = 'Nezávislí',
}

const mapPartyToColor = new Map([
  [Party.ODS, '#004494'],
  [Party.KSCM, 'red'],
  [Party.SPD, 'brown'],
  [Party.PIR, 'black'],
  [Party.KDU, 'yellow'],
  [Party.CSSD, 'orange'],
  [Party.ANO, '#4967ee'],
  [Party.NPB, '#a0a0a0'],
  [Party.NEZ, '#cccccc'],
]);

const DEFAULT_COLOR = 'gray';

export const Detail: React.FC<HomeProps> = () => {
  const { detailId } = useParams();
  const [result, setResult] = useState<VoteResult | null>(null);
  const [subject, setSubject] = useState<string>('');
  const [date, setDate] = useState<Date | null>(null);
  const [votes, setVotes] = useState<any>([]);
  const [parties, setParties] = useState<Array<string>>(['']);
  const [yesVotes, setYesVotes] = useState<number>(0);
  const [noVotes, setNoVotes] = useState<number>(0);
  const [numberOfMeeting, setNumberOfMeeting] = useState<string>('');
  const [numberOfVoting, setNumberOfVoting] = useState<number>(0);

  useEffect(() => {
    api.get(`voting-record-full/${detailId}`).then((response: any) => {
      const data = response.data.data;
      setResult(
        data.info.result === 'accepted'
          ? VoteResult.Accepted
          : VoteResult.Rejected
      );
      setSubject(data.info.subject);
      setDate(new Date(data.info.datetime));
      setVotes(data.votes);
      setParties(data.parties);
      setYesVotes(data.info.yes);
      setNoVotes(data.info.no);
      setNumberOfMeeting(data.info.code);
      setNumberOfVoting(data.info.number);
    });
  }, [detailId]);

  const votingPersons = votes.map((vote: any) => ({
    name: correctName(vote.representativeLink.name),
    party: vote.representativeLink.party,
    vote: vote.result,
  }));

  const votingPersonsFormatted = votingPersons.map((person: any) => {
    return {
      ...person,
      color: mapPartyToColor.get(person.party) ?? DEFAULT_COLOR,
    };
  });

  const getPersonsElementsByVote = (value: string) => {
    const personsByVoteValue = votingPersonsFormatted.filter((person: any) => {
      return person.vote === value;
    });

    const sortedPersonsByVoteValue = sortPersonsByParties(personsByVoteValue);
    const voteElements = createVoteElements(sortedPersonsByVoteValue);

    return voteElements;
  };

  const sortPersonsByParties = (personsByVoteValue: any) => {
    const personsByParties: any = [];

    parties.forEach((party) => {
      const personsOfParty = personsByVoteValue.filter((person: any) => {
        return person.party === party;
      });

      personsByParties.push(personsOfParty);
    });

    const sortedPersons = personsByParties.sort((a: any, b: any) => {
      return b.length - a.length;
    });

    const mergedSortedPersons = [].concat.apply([], sortedPersons);

    return mergedSortedPersons;
  };

  const createVoteElements = (persons: any) => {
    const voteElements = persons.map((person: any) => {
      return (
        <StyledVote key={person.name} color={person.color}>
          <StyledVoteDetail>
            <StyledVoteDetailParty>{person.party}</StyledVoteDetailParty>
            {person.name}
          </StyledVoteDetail>
        </StyledVote>
      );
    });

    return voteElements;
  };

  const yesVoteElements = getPersonsElementsByVote('yes');
  const noVoteElements = getPersonsElementsByVote('no');

  const yesFullVotes = votes.filter((v: any) => v.result === 'yes');
  const noFullVotes = votes.filter((v: any) => v.result === 'no');

  const getPartyColor = (party: Party) => {
    return mapPartyToColor.get(party) ?? DEFAULT_COLOR;
  };

  return (
    <StyledPageWrapper>
      <DetailHeader
        subject={subject}
        result={result}
        yesVotes={yesVotes}
        noVotes={noVotes}
        meeting={numberOfMeeting}
        poll={numberOfVoting}
        date={date}
      />
      <StyledPageContainer>
        <StyledPageContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Visualization
                title="Hlasování PRO"
                elements={yesVoteElements}
                votes={yesFullVotes}
                option={VoteResult.Accepted}
                getPartyColor={getPartyColor}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Visualization
                title="Hlasování PROTI"
                elements={noVoteElements}
                votes={noFullVotes}
                option={VoteResult.Rejected}
                getPartyColor={getPartyColor}
              />
            </Grid>
          </Grid>
        </StyledPageContent>
      </StyledPageContainer>
    </StyledPageWrapper>
  );
};
